import React from 'react';
import { detectUserTimezone, normalizeTimezone } from '@/utils/timezoneDetection';
import { fromZonedTime } from 'date-fns-tz';
import axiosInstance from '@/config/axiosInstance';
import { endpoints } from '@/config/apiEndpoints';

export type TZContext = {
  timezone: string;
  setTimezone: (tz: string) => void;
  formatDateTime: (utcIso?: string | null, opts?: Intl.DateTimeFormatOptions) => string;
  toDateInputValue: (utcIso?: string | null) => string;
  // Convert a `datetime-local` value (YYYY-MM-DDTHH:MM) in the selected timezone to local datetime string
  localToUtcIso: (localDateTime?: string | null) => string | null;
};

const defaultTZ = 'UTC';

const looksLikeZonedIso = (value: string): boolean => /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

export const TimezoneContext = React.createContext<TZContext>({
  timezone: defaultTZ,
  setTimezone: () => {},
  formatDateTime: () => '',
  toDateInputValue: () => '',
  localToUtcIso: () => null,
});

export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ IMPROVED: Initialize with localStorage only (no system detection here)
  // This ensures we don't prematurely save system-detected timezone to localStorage
  const initial = React.useMemo(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('user_timezone') : null;
    const normalizedSaved = normalizeTimezone(saved);
    return normalizedSaved ?? defaultTZ;
  }, []);

  const [timezone, setTimezoneState] = React.useState<string>(initial);

  // Sync timezone with localStorage whenever it changes
  React.useEffect(() => {
    try {
      localStorage.setItem('user_timezone', timezone);
    } catch (_) {}
  }, [timezone]);

  // ✅ IMPROVED: On mount, prioritize user's profile timezone over localStorage
  // This runs on every mount to check for the user's stored timezone preference
  React.useEffect(() => {
    const syncUserTimezone = async () => {
      try {
        const token =
          (typeof window !== 'undefined' && sessionStorage.getItem('access_token')) ||
          (typeof window !== 'undefined' && localStorage.getItem('access_token'));
        
        // ✅ NEW: If user is logged in, always check their profile timezone first
        if (token) {
          try {
            const res = await axiosInstance.get(endpoints.me);
            const user = res.data?.user ?? res.data;

            // ✅ Priority 1: Use user's stored timezone from profile
            const profileTz = normalizeTimezone(user?.timezone);
            if (profileTz) {
              setTimezoneState(profileTz);
              try {
                localStorage.setItem('user_timezone', profileTz);
                sessionStorage.setItem('user_timezone', profileTz);
              } catch (_) {}
              return;
            }

            // ✅ Priority 2: If user profile has no timezone, detect from system
            const detected = normalizeTimezone(detectUserTimezone()) ?? defaultTZ;
            setTimezoneState(detected);
            try {
              localStorage.setItem('user_timezone', detected);
              sessionStorage.setItem('user_timezone', detected);
            } catch (_) {}
            return;
          } catch (e) {
            // If API fails but user is logged in, keep current state
            console.warn('Failed to sync user timezone from profile', e);
          }
        }

        // ✅ Priority 3: If not logged in and no localStorage, detect from system
        if (!token && initial === defaultTZ) {
          const detected = normalizeTimezone(detectUserTimezone()) ?? defaultTZ;
          setTimezoneState(detected);
        }
      } catch (e) {
        // Silently fail - user might not be logged in yet
      }
    };

    syncUserTimezone();
  }, [initial]);

  const setTimezone = (tz: string) => {
    setTimezoneState(normalizeTimezone(tz) ?? defaultTZ);
  };

  const formatDateTime = (
    value?: string | null,
    opts: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }
  ) => {
    if (!value) return '';
    try {
      const date = looksLikeZonedIso(value)
        ? new Date(value)
        : fromZonedTime(value, timezone);
      return new Intl.DateTimeFormat(undefined, { timeZone: timezone, ...opts }).format(date);
    } catch (e) {
      return value;
    }
  };

  const toDateInputValue = (localIso?: string | null) => {
    if (!localIso) return '';
    try {
      const date = looksLikeZonedIso(localIso)
        ? new Date(localIso)
        : fromZonedTime(localIso, timezone);
      const parts = new Intl.DateTimeFormat('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
      return parts.replace(' ', 'T');
    } catch (e) {
      return '';
    }
  };

  const localToUtcIso = (localDateTime?: string | null) => {
    if (!localDateTime) return null;
    try {
      const date = fromZonedTime(localDateTime, timezone);
      return date.toISOString();
    } catch (e) {
      return null;
    }
  };

  const ctx: TZContext = {
    timezone,
    setTimezone,
    formatDateTime,
    toDateInputValue,
    localToUtcIso,
  };

  return <TimezoneContext.Provider value={ctx}>{children}</TimezoneContext.Provider>;
};

export const useTimezone = () => React.useContext(TimezoneContext);
